import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-router';
import type { TTermsBase } from '@onesource/schemas';
import { useAcceptSpaceTerms } from '@onesource/services';
import DOMPurify from 'dompurify';
import { FC, useCallback, useMemo } from 'react';

export interface FeedTermsAcceptanceDialogProps {
  spaceId: string;
  terms: TTermsBase;
}

/**
 * Blocking modal shown when a user enters a feed they haven't accepted the
 * latest T&C version for. Backdrop click and ESC are disabled — only "I Agree"
 * resolves it; "Decline" navigates the user away from the feed.
 */
export const FeedTermsAcceptanceDialog: FC<FeedTermsAcceptanceDialogProps> = ({ spaceId, terms }) => {
  const navigate = useNavigate();
  const { mutateAsync: acceptTerms, isPending } = useAcceptSpaceTerms(spaceId);

  // Sanitize, then force every rendered link to open in a new tab. The Quill
  // editor in the dashboard does not write `target` itself, so we apply it at
  // render time instead of forcing the admin to remember it.
  const sanitizedBody = useMemo(() => {
    const clean = DOMPurify.sanitize(terms.body);
    if (typeof window === 'undefined') {
      return clean;
    }
    const doc = new DOMParser().parseFromString(clean, 'text/html');
    doc.querySelectorAll('a[href]').forEach(anchor => {
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener noreferrer');
    });
    return doc.body.innerHTML;
  }, [terms.body]);
  const heading = terms.heading || 'Terms & Conditions';

  const handleAgree = useCallback(async () => {
    await acceptTerms({ version: terms.version });
  }, [acceptTerms, terms.version]);

  const handleDecline = useCallback(() => {
    navigate({ to: '/' });
  }, [navigate]);

  return (
    <Dialog
      open
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      onClose={(_, reason) => {
        if (reason === 'backdropClick') {
          return;
        }
      }}
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Typography
          component="h2"
          sx={{
            fontFamily: 'Figtree, sans-serif',
            fontWeight: 700,
            fontSize: 20,
            lineHeight: 1.3,
            color: 'text.primary',
          }}
        >
          {heading}
        </Typography>
      </Box>
      <DialogContent sx={{ px: 3, py: 1 }}>
        <Box
          dangerouslySetInnerHTML={{ __html: sanitizedBody }}
          sx={{
            fontFamily: 'Figtree, sans-serif',
            fontSize: 14,
            lineHeight: 1.55,
            color: 'text.primary',
            '& > :first-of-type': { mt: 0 },
            '& > :last-child': { mb: 0 },
            '& p': { my: 1 },
            '& a': { color: 'primary.main', wordBreak: 'break-word' },
          }}
        />
        {terms.termsUrl && (
          <Typography
            component="a"
            href={terms.termsUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'inline-block',
              mt: 1.5,
              fontFamily: 'Figtree, sans-serif',
              fontSize: 13,
              fontWeight: 600,
              color: 'primary.main',
              textDecoration: 'underline',
              '&:hover': { textDecoration: 'none' },
            }}
          >
            Read the full terms
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
        <Button onClick={handleDecline} disabled={isPending} variant="outlined" color="inherit">
          Decline
        </Button>
        <Button
          onClick={handleAgree}
          disabled={isPending}
          variant="contained"
          startIcon={isPending ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {isPending ? 'Saving…' : 'I Agree'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
